import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useDrag } from '@use-gesture/react';
import { useSpring } from '@react-spring/web';

import { imActions } from '$store/im';
import { MessageModel } from '$store/models';
import { haptic } from '$utils';

export const REPLY_SWIPE_THRESHOLD = 40;
export const REPLY_SWIPE_MAX = 70;

export function useReplySwipe(
  peerId: number,
  message: MessageModel,
  enabled: boolean,
  onDragStart?: () => void,
) {
  const dispatch = useDispatch();
  const didSwipe = useRef(false);
  const hapticFired = useRef(false);
  const [{ x }, swipeApi] = useSpring(() => ({ x: 0 }));

  const reply = useCallback(() => {
    dispatch(
      imActions.chatSetReplyTo({
        peerId,
        replyTo: {
          id: message.id,
          fromId: message.fromId,
          text: message.text,
          attachments: message.attachments,
        },
      }),
    );
  }, [dispatch, peerId, message]);

  const bindSwipe = useDrag(
    ({ down, movement: [mx], first, last }) => {
      if (first) {
        onDragStart?.();
        hapticFired.current = false;
      }

      const offset = Math.max(-REPLY_SWIPE_MAX, Math.min(0, mx));

      if (down) {
        if (Math.abs(mx) > 5) {
          didSwipe.current = true;
        }
        if (offset <= -REPLY_SWIPE_THRESHOLD && !hapticFired.current) {
          hapticFired.current = true;
          haptic('light');
        }
        swipeApi.start({ x: offset, immediate: true });
      } else {
        if (mx <= -REPLY_SWIPE_THRESHOLD) {
          reply();
        }
        swipeApi.start({ x: 0 });
        if (last) {
          setTimeout(() => {
            didSwipe.current = false;
          }, 50);
        }
      }
    },
    {
      axis: 'x',
      filterTaps: true,
      enabled,
    },
  );

  return { bindSwipe, x, didSwipe, reply };
}
